/**
 * Browser-side Life360 transport for the "Katamobiil" card.
 *
 * Life360's private API sits behind Cloudflare bot management that rejects
 * every non-browser TLS fingerprint, so the hub server cannot call it. The
 * browser can, but Life360 enables CORS per route and from a non-Life360 origin
 * only `/circles/{id}/members/history` is readable (`/circles` and
 * `/circles/{id}/members` fail the preflight). So this module does that single
 * GET with the token the dashboard payload supplied and hands the raw JSON to
 * the server's `observe` endpoint, which picks the newest location per member
 * and builds the card payload. The server stays the brain; the browser is only
 * the transport.
 *
 * Plain module (no React) so the card component stays presentational.
 */
import type { Life360CardPayload } from '../../../../../../server/controllers/portal/dashboard-card-payloads.js'
import { portalPost } from '../../../utils/api.js'

const BASE = 'https://api-cloudfront.life360.com/v3'
const OBSERVE_URL = '/api/portal/dashboard/life360/observe'

export interface Life360ClientConfig {
    token: string
    circleId: string
}

const failed = (message: string): Life360CardPayload => ({ status: 'error', message })

/**
 * Fetch the circle's location history from Life360 in the browser and turn it
 * into the card payload via the server. Never throws — failures become `error`
 * payloads (the card shows its generic error text; details stay in the console).
 */
export const fetchLife360Payload = async (
    config: Life360ClientConfig,
): Promise<Life360CardPayload> => {
    try {
        const path = `/circles/${config.circleId}/members/history`
        const res = await fetch(`${BASE}${path}`, {
            headers: { Accept: 'application/json', Authorization: `Bearer ${config.token}` },
        })
        if (res.status === 401) throw new Error('token rejected (401)')
        if (!res.ok) throw new Error(`GET ${path} failed (${res.status})`)
        const history: unknown = await res.json()

        const posted = await portalPost<Life360CardPayload>(OBSERVE_URL, {
            circleName: '',
            members: history,
        })
        return posted.ok ? posted.data : failed(posted.error)
    } catch (error) {
        console.error('[Life360] browser fetch failed:', error)
        return failed(error instanceof Error ? error.message : 'Life360 request failed')
    }
}
